import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import clsx from 'clsx';
import { Editor } from '@tinymce/tinymce-react';
import FuseChipSelect from '@fuse/core/FuseChipSelect';
import {
  EMAIL_FIELD_OPTIONS,
  ALL_FIELD_OPTIONS,
  FIELD_CONFIG,
  SUBMISSION_ACTION_OPTIONS,
  CREATE_USER_REQUIRED_FIELDS,
  isFieldRequired,
  missingCreateUserFields
} from 'app/constants/formFields';
import { getTemplates, selectTemplates } from '../store/templateSlice';

const useStyles = makeStyles({
  emailFieldSelect: {
    '& .wdw-valueContainer': {
      padding: '12px !important',
      minHeight: 53
    },
    '& .fuse-chip-select__input input': {
      caretColor: 'transparent'
    },
    '& .MuiInputBase-root': {
      cursor: 'pointer !important'
    },
    '& .MuiInputBase-input': {
      cursor: 'pointer !important'
    },
    '& .fuse-chip-select__single-value': {
      fontSize: 14
    },
    '& .fuse-chip-select__placeholder': {
      fontSize: 14
    },
    '& .wdw-valueContainer .MuiTypography-root': {
      fontSize: '14px !important'
    }
  },
  buttonBase: {
    width: 40,
    height: 53,
    minWidth: 40,
    minHeight: 53,
    borderRadius: 8,
    border: '1px solid',
    padding: 0
  },
  addButton: {
    backgroundColor: '#fff',
    color: '#bdbdbd',
    borderColor: '#bdbdbd',
    '&:hover': {
      backgroundColor: '#d9d9d9',
      color: '#fff',
      borderColor: 'transparent'
    }
  },
  deleteButton: {
    backgroundColor: '#fff',
    color: '#e53935',
    borderColor: '#e53935',
    '&:hover': {
      backgroundColor: '#e53935',
      color: '#fff'
    }
  },
  infoButton: {
    width: 18,
    height: 18,
    minWidth: 18,
    minHeight: 18,
    borderRadius: '50%',
    border: '1.5px solid rgba(0,0,0,0.4)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(0,0,0,0.4)',
    cursor: 'default',
    flexShrink: 0,
    lineHeight: 1,
    userSelect: 'none',
    fontFamily: 'Arial, sans-serif'
  },
  readOnlyInput: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(0,0,0,0.04)'
    },
    '& .MuiInputBase-input': {
      color: 'rgba(0,0,0,0.54)'
    }
  },
  inputRow: {
    // The drop indicator is drawn as a border on the row itself, so every row
    // reserves the space up front — adding it on hover would shift the list.
    borderTop: '2px solid transparent',
    borderBottom: '2px solid transparent'
  },
  dragHandle: {
    width: 26,
    minWidth: 26,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#bdbdbd',
    cursor: 'grab',
    flexShrink: 0,
    borderRadius: 4,
    outline: 'none',
    '&:active': {
      cursor: 'grabbing'
    },
    '&:hover': {
      color: '#8c8c8c'
    },
    '&:focus-visible': {
      color: '#8c8c8c',
      boxShadow: '0 0 0 2px rgba(203,30,43,0.4)'
    }
  },
  dragging: {
    opacity: 0.4
  },
  dropAbove: {
    borderTopColor: '#cb1e2b'
  },
  dropBelow: {
    borderBottomColor: '#cb1e2b'
  },
  requiredCheckbox: {
    marginLeft: 0,
    marginRight: 0,
    flexShrink: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      fontFamily: 'Arial, sans-serif',
      color: 'rgba(0,0,0,0.54)'
    },
    // `iloan.css` paints every `.Mui-disabled` grey with `!important`. That is
    // meant for buttons; on a locked checkbox it turns the box and the word
    // "Required" into a grey block. Undone here only, so the rule keeps working
    // everywhere it was written for.
    '&.Mui-disabled, & .Mui-disabled': {
      backgroundColor: 'transparent !important'
    }
  }
});

/**
 * A switched-off template is still selectable — it may be about to be turned
 * back on — but it says so, here and on the chip, because it will not be sent.
 */
const templateOption = t => ({
  value: t._id,
  label: t.active ? t.name : `${t.name} (Inactive)`
});

/**
 * Spells out where each connected template will actually be delivered.
 *
 * The two slots treat `sendTo` differently — the confirmation one always
 * replaces it with the submitter's address — and nothing on this screen said
 * so, which is how a notification meant for HR arrived in the new hire's inbox.
 * A template that cannot be delivered at all is called out in red rather than
 * dropped in silence.
 */
function TemplateDelivery({ ids, templates, toSubmitter, hasEmailField }) {
  const selected = (ids || []).map(id => templates.find(t => t._id === id)).filter(Boolean);

  if (!selected.length) return null;

  // Without an Email Field the form never learns the submitter's address, so a
  // confirmation is skipped before any template is even looked at.
  const noSubmitter = toSubmitter && !hasEmailField;

  // The offset matches the createUser warning further down, so hints under a
  // select line up wherever they appear on this screen.
  return (
    <ul style={{ margin: '8px 0 16px 15px', padding: 0, listStyle: 'none' }}>
      {selected.map(t => {
        // Red means nothing will arrive; amber means it will, but not where the
        // template itself says.
        let note = null;
        if (!t.active) {
          note = { text: 'switched off in Email Templates, so nothing is sent', color: '#cf1e2b' };
        } else if (noSubmitter) {
          note = { text: 'this form has no Email Field, so nothing is sent', color: '#cf1e2b' };
        } else if (!toSubmitter && !t.sendTo) {
          note = { text: 'no Send to address, so nothing is sent', color: '#cf1e2b' };
        } else if (toSubmitter && t.sendTo) {
          note = {
            text: `the person who submits the form — its own Send to (${t.sendTo}) is ignored here`,
            color: '#b26a00'
          };
        }

        return (
          <li
            key={t._id}
            style={{
              fontSize: 12,
              lineHeight: 1.6,
              color: note ? note.color : 'rgba(0,0,0,0.54)',
              fontFamily: 'Arial, sans-serif'
            }}
          >
            <strong>{t.name}</strong>
            {' → '}
            {note ? note.text : t.sendTo || 'the person who submits the form'}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A "Required" box that explains itself when it cannot be unticked.
 *
 * The two locked cases are not preferences — an optional Email Field leaves the
 * form with nowhere to deliver, and an optional name leaves a `createUser` form
 * producing submissions the action can only fail on. Saying so beats a checkbox
 * that simply refuses to move.
 */
function RequiredToggle({ className, checked, disabled, hint, onChange }) {
  const control = (
    <FormControlLabel
      className={className}
      control={
        <Checkbox size="small" color="secondary" checked={checked} disabled={disabled} onChange={onChange} />
      }
      label="Required"
    />
  );

  // A disabled control emits no events, so the tooltip needs a wrapper to listen on.
  return hint ? (
    <Tooltip title={hint} placement="top">
      <span>{control}</span>
    </Tooltip>
  ) : (
    control
  );
}

function FormGeneratorForm({ form, setForm }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const templates = useSelector(selectTemplates);

  // `dragIndex` is a ref rather than state because `onDragOver` needs to know the
  // source row synchronously, before a re-render could have delivered it.
  const dragIndex = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  // Rows are only draggable while the handle is held. Marking them draggable
  // permanently would take focus and text selection away from the select inside.
  const [draggableIndex, setDraggableIndex] = useState(null);
  const handleRefs = useRef([]);

  useEffect(() => {
    dispatch(getTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (!form.slug) {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      const slug = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      setForm(prev => ({ ...prev, slug, url: prev.url || slug }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const additionalInputs = form.additionalInputs || [];
  const optionalInputs = form.optionalInputs || [];
  const missingFields = missingCreateUserFields(form);

  /**
   * Keeps `optionalInputs` to keys the form still collects, so swapping or
   * removing a row cannot leave a flag behind for a field nobody fills.
   */
  const syncOptional = nextInputs => optionalInputs.filter(key => nextInputs.includes(key));

  const setInputs = nextInputs =>
    setForm({ ...form, additionalInputs: nextInputs, optionalInputs: syncOptional(nextInputs) });

  const handleAddInput = () => {
    setInputs([...additionalInputs, '']);
  };

  const handleRemoveInput = index => {
    setInputs(additionalInputs.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, value) => {
    setInputs(additionalInputs.map((v, i) => (i === index ? value : v)));
  };

  const handleToggleRequired = key => {
    setForm({
      ...form,
      optionalInputs: isFieldRequired(form, key)
        ? [...optionalInputs, key]
        : optionalInputs.filter(k => k !== key)
    });
  };

  /** Empty when the box can be unticked; the tooltip only exists to explain a lock. */
  const requiredHint = key => {
    if (!key) return '';
    if (form.submissionAction === 'createUser' && CREATE_USER_REQUIRED_FIELDS.includes(key)) {
      return 'Creating a platform user needs this field, so it cannot be optional.';
    }
    return '';
  };

  const resetDrag = () => {
    dragIndex.current = null;
    setDraggingIndex(null);
    setDropIndex(null);
    setDraggableIndex(null);
  };

  const handleDragStart = index => event => {
    dragIndex.current = index;
    setDraggingIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a drag at all unless the payload is set.
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = index => event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragIndex.current === null) return;
    // Cleared over the row being dragged, so the indicator does not linger on
    // whichever row the pointer passed through last.
    setDropIndex(dragIndex.current === index ? null : index);
  };

  const moveInput = (from, to) => {
    if (from === to || to < 0 || to >= additionalInputs.length) return;

    const reordered = [...additionalInputs];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    // Only `additionalInputs` carries the order; `optionalInputs` is keyed by
    // field, so moving a row leaves what is required alone.
    setForm({ ...form, additionalInputs: reordered });

    // Rows are keyed by position, so the handle that was focused now belongs to
    // whichever row took this slot. Following the row keeps a second keypress
    // moving the same input instead of its new neighbour.
    requestAnimationFrame(() => {
      const handle = handleRefs.current[to];
      if (handle) handle.focus();
    });
  };

  const handleDrop = index => event => {
    event.preventDefault();
    const from = dragIndex.current;
    resetDrag();
    if (from !== null) moveInput(from, index);
  };

  /** Arrow keys do what the handle does, for anyone not using a mouse. */
  const handleHandleKeyDown = index => event => {
    const delta = { ArrowUp: -1, ArrowDown: 1 }[event.key];
    if (!delta) return;
    event.preventDefault();
    moveInput(index, index + delta);
  };

  const rowDropClass = index => {
    if (dropIndex !== index) return null;
    // The line is drawn on the side the row would land on, so the indicator
    // reads as the gap it is about to fill.
    return draggingIndex > index ? classes.dropAbove : classes.dropBelow;
  };

  return (
    <div className="flex flex-1 justify-center mt-20 mb-20">
      <div className="w-full max-w-xl mx-auto p-24 flex flex-col gap-24">
        <TextField
          label="Form Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          variant="outlined"
          fullWidth
        />

        <div>
          <p
            style={{
              marginBottom: 8,
              color: 'rgba(0,0,0,0.85)',
              fontSize: '10.5px',
              lineHeight: 1,
              marginLeft: 15,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Form Description
          </p>
          <Editor
            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
            value={form.description}
            onEditorChange={content => setForm({ ...form, description: content })}
            init={{
              height: 400,
              menubar: false,
              plugins: 'lists link media',
              toolbar: [
                'undo redo | blocks fontfamily fontsize | bold italic underline forecolor | alignmenu bullist numlist | removeformat | link | addmedia | variables'
              ],
              content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }',
              setup: editor => {
                editor.ui.registry.addButton('addmedia', {
                  icon: 'embed',
                  text: 'Add Media',
                  tooltip: 'Insert media',
                  onAction: () => editor.execCommand('mceMedia')
                });

                editor.ui.registry.addMenuButton('alignmenu', {
                  icon: 'align-left',
                  tooltip: 'Alignment',
                  fetch: callback => {
                    callback([
                      {
                        type: 'menuitem',
                        text: 'Align Left',
                        icon: 'align-left',
                        onAction: () => editor.execCommand('JustifyLeft')
                      },
                      {
                        type: 'menuitem',
                        text: 'Align Center',
                        icon: 'align-center',
                        onAction: () => editor.execCommand('JustifyCenter')
                      },
                      {
                        type: 'menuitem',
                        text: 'Align Right',
                        icon: 'align-right',
                        onAction: () => editor.execCommand('JustifyRight')
                      },
                      {
                        type: 'menuitem',
                        text: 'Justify',
                        icon: 'align-justify',
                        onAction: () => editor.execCommand('JustifyFull')
                      }
                    ]);
                  }
                });
              }
            }}
          />
        </div>

        <div
          style={{
            position: 'relative',
            border: '1px solid rgba(0,0,0,0.23)',
            borderRadius: 4,
            padding: '18.5px 14px'
          }}
        >
          <p
            style={{
              position: 'absolute',
              top: -7,
              left: 10,
              margin: 0,
              padding: '0 4px',
              backgroundColor: '#fff',
              color: 'rgba(0,0,0,0.54)',
              fontSize: '10.5px',
              lineHeight: 1,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Form Inputs
          </p>

          <div className={clsx('flex items-center gap-8 my-16', classes.inputRow)}>
            {/* Aligns the email row with the draggable ones below, which start
                with a handle. The email field is always rendered first on the
                public form, so it is not part of the ordering. */}
            <span className={classes.dragHandle} />
            <FuseChipSelect
              isMulti={false}
              className={`w-full ${classes.emailFieldSelect}`}
              value={EMAIL_FIELD_OPTIONS.find(o => o.value === form.emailField) || null}
              options={EMAIL_FIELD_OPTIONS}
              onChange={selected => setForm({ ...form, emailField: selected ? selected.value : '' })}
              textFieldProps={{
                label: 'Email Field',
                InputLabelProps: { shrink: true },
                variant: 'outlined'
              }}
            />
            <RequiredToggle
              className={classes.requiredCheckbox}
              checked
              disabled
              hint="The email field is the address the form is delivered to, so it is always required."
              onChange={() => {}}
            />
            {additionalInputs.length === 0 && (
              <IconButton className={clsx(classes.buttonBase, classes.addButton)} onClick={handleAddInput}>
                <Icon fontSize="small">add</Icon>
              </IconButton>
            )}
            <Tooltip
              title="The email field determines which address will be used to send the form link to the recipient. Additional fields define what information the form will collect from them. Drag a row by its handle to change the order they appear in, and untick Required to let it be left blank."
              placement="top"
              arrow
            >
              <span className={classes.infoButton}>i</span>
            </Tooltip>
          </div>

          {additionalInputs.map((fieldValue, index) => (
            <div
              key={index}
              className={clsx(
                'flex items-center gap-8 my-16',
                classes.inputRow,
                draggingIndex === index && classes.dragging,
                rowDropClass(index)
              )}
              draggable={draggableIndex === index}
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={resetDrag}
            >
              <Tooltip title="Drag to reorder, or use the arrow keys" placement="top">
                {/* A span rather than an IconButton: Firefox will not start a
                    drag when the gesture begins on a form control, so the whole
                    row would stay put. */}
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Reorder this input"
                  ref={element => {
                    handleRefs.current[index] = element;
                  }}
                  className={classes.dragHandle}
                  onMouseDown={() => setDraggableIndex(index)}
                  onMouseUp={() => setDraggableIndex(null)}
                  onKeyDown={handleHandleKeyDown(index)}
                >
                  <Icon fontSize="small">drag_indicator</Icon>
                </span>
              </Tooltip>
              <FuseChipSelect
                isMulti={false}
                className={`w-full ${classes.emailFieldSelect}`}
                value={ALL_FIELD_OPTIONS.find(o => o.value === fieldValue) || null}
                options={ALL_FIELD_OPTIONS}
                onChange={selected => handleInputChange(index, selected ? selected.value : '')}
                textFieldProps={{
                  variant: 'outlined'
                }}
              />
              <RequiredToggle
                className={classes.requiredCheckbox}
                checked={isFieldRequired(form, fieldValue)}
                disabled={!fieldValue || Boolean(requiredHint(fieldValue))}
                hint={requiredHint(fieldValue)}
                onChange={() => handleToggleRequired(fieldValue)}
              />
              {index === additionalInputs.length - 1 && (
                <IconButton className={clsx(classes.buttonBase, classes.addButton)} onClick={handleAddInput}>
                  <Icon fontSize="small">add</Icon>
                </IconButton>
              )}
              <IconButton
                className={clsx(classes.buttonBase, classes.deleteButton)}
                onClick={() => handleRemoveInput(index)}
              >
                <Icon fontSize="small">close</Icon>
              </IconButton>
            </div>
          ))}
        </div>

        <FuseChipSelect
          isMulti
          className={`w-full ${classes.emailFieldSelect}`}
          value={(form.emailTemplates || [])
            .map(id => {
              const t = templates.find(tpl => tpl._id === id);
              return t ? templateOption(t) : null;
            })
            .filter(Boolean)}
          options={templates.map(templateOption)}
          onChange={selected => {
            const ids = (selected || []).map(s => s.value);
            setForm({ ...form, emailTemplates: ids });
          }}
          textFieldProps={{
            label: 'Notification Email',
            InputLabelProps: { shrink: true },
            variant: 'outlined'
          }}
          styles={{
            valueContainer: base => ({ ...base, maxHeight: 53, overflowY: 'auto' })
          }}
        />

        <TemplateDelivery
          ids={form.emailTemplates}
          templates={templates}
          toSubmitter={false}
          hasEmailField={Boolean(form.emailField)}
        />

        <FuseChipSelect
          isMulti
          className={`w-full ${classes.emailFieldSelect}`}
          value={(form.secondaryEmailTemplates || [])
            .map(id => {
              const t = templates.find(tpl => tpl._id === id);
              return t ? templateOption(t) : null;
            })
            .filter(Boolean)}
          options={templates.map(templateOption)}
          onChange={selected => {
            const ids = (selected || []).map(s => s.value);
            setForm({ ...form, secondaryEmailTemplates: ids });
          }}
          textFieldProps={{
            label: 'Submission Confirmation Email Template Connection',
            InputLabelProps: { shrink: true },
            variant: 'outlined'
          }}
        />

        <TemplateDelivery
          ids={form.secondaryEmailTemplates}
          templates={templates}
          toSubmitter
          hasEmailField={Boolean(form.emailField)}
        />

        <div>
          <FuseChipSelect
            isMulti={false}
            className={`w-full ${classes.emailFieldSelect}`}
            value={SUBMISSION_ACTION_OPTIONS.find(o => o.value === (form.submissionAction || 'none'))}
            options={SUBMISSION_ACTION_OPTIONS}
            onChange={selected => setForm({ ...form, submissionAction: selected ? selected.value : 'none' })}
            textFieldProps={{
              label: 'On Submit',
              InputLabelProps: { shrink: true },
              variant: 'outlined'
            }}
          />
          {form.submissionAction === 'createUser' && missingFields.length > 0 && (
            <p
              style={{
                marginTop: 8,
                marginLeft: 15,
                color: '#cf1e2b',
                fontSize: '12px',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              To create a user this form also needs: {missingFields.map(key => FIELD_CONFIG[key].label).join(', ')}.
            </p>
          )}
        </div>

        <TextField
          fullWidth
          label="Internal ID"
          variant="outlined"
          value={form.slug ? `${form.slug}` : ''}
          className={classes.readOnlyInput}
          InputProps={{ readOnly: true }}
        />

        <TextField
          fullWidth
          label="URL"
          variant="outlined"
          value={form.url || ''}
          onChange={e => {
            const sanitized = e.target.value
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/^\/+/, '')
              .replace(/[^a-z0-9\-_.]/g, '');
            setForm({ ...form, url: sanitized });
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">/</InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Copy to clipboard" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (form.url) {
                        navigator.clipboard.writeText(`${window.location.origin}/${form.url}`);
                      }
                    }}
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            )
          }}
        />
      </div>
    </div>
  );
}

export default FormGeneratorForm;
